import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Package, MapPin, User, LogOut, Trash2, Plus, Heart, Star, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatINR } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { toast } from "sonner";

const TABS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "orders", label: "Orders", icon: Package },
  { key: "addresses", label: "Addresses", icon: MapPin },
  { key: "wishlist", label: "Wishlist", icon: Heart },
];

export default function Account() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("profile");
  const nav = useNavigate();

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-brand-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: "/account" }} />;

  const onLogout = async () => {
    await logout();
    toast.success("Logged out. See you soon!");
    nav("/");
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="account-page">
      <div className="flex flex-wrap items-center gap-6 mb-10">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover border-2 border-brand-gold" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-brand-maroon text-brand-ivory flex items-center justify-center font-serif-brand text-2xl">{user.name?.[0] || "B"}</div>
        )}
        <div>
          <p className="label-tiny text-brand-gold">Namaste</p>
          <h1 className="font-serif-brand text-4xl text-brand-charcoal" data-testid="account-name">{user.name}</h1>
          <p className="text-sm text-brand-muted">{user.email}</p>
        </div>
        <button onClick={onLogout} className="ml-auto text-xs tracking-[0.2em] uppercase font-medium px-4 py-2 border border-brand-charcoal/50 hover:bg-brand-maroon hover:text-brand-ivory hover:border-brand-maroon transition-colors duration-200 flex items-center gap-2" data-testid="logout-btn">
          <LogOut size={14} /> Logout
        </button>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="flex lg:flex-col gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-3 px-4 py-3 text-left border transition-colors duration-200 shrink-0 ${tab === t.key ? "border-brand-maroon bg-brand-ivory-soft text-brand-maroon" : "border-transparent text-brand-charcoal hover:border-brand-gold/30"}`} data-testid={`account-tab-${t.key}`}>
              <t.icon size={16} strokeWidth={1.4} /> <span className="text-sm">{t.label}</span>
            </button>
          ))}
        </aside>

        <div>
          {tab === "profile" && <ProfileTab user={user} />}
          {tab === "orders" && <OrdersTab />}
          {tab === "addresses" && <AddressesTab />}
          {tab === "wishlist" && <WishlistTab />}
        </div>
      </div>
    </div>
  );
}

const ProfileTab = ({ user }) => (
  <div className="border border-brand-gold/25 p-8 bg-brand-ivory-soft" data-testid="profile-tab">
    <p className="label-tiny text-brand-gold">Your Profile</p>
    <h2 className="font-serif-brand text-2xl text-brand-charcoal mt-2 mb-6">Personal Details</h2>
    <dl className="space-y-4 text-sm">
      <div className="grid grid-cols-3 gap-3 py-2 border-b border-brand-gold/20"><dt className="label-tiny text-brand-muted">Name</dt><dd className="col-span-2 text-brand-charcoal">{user.name}</dd></div>
      <div className="grid grid-cols-3 gap-3 py-2 border-b border-brand-gold/20"><dt className="label-tiny text-brand-muted">Email</dt><dd className="col-span-2 text-brand-charcoal">{user.email}</dd></div>
      <div className="grid grid-cols-3 gap-3 py-2 border-b border-brand-gold/20"><dt className="label-tiny text-brand-muted">Member Since</dt><dd className="col-span-2 text-brand-charcoal">{new Date(user.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</dd></div>
      <div className="grid grid-cols-3 gap-3 py-2"><dt className="label-tiny text-brand-muted">Signed in via</dt><dd className="col-span-2 text-brand-charcoal">Google</dd></div>
    </dl>
    <p className="label-tiny text-brand-muted mt-6">Managed by Google. Update your name & photo from your Google account.</p>
  </div>
);

const OrdersTab = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/me/orders", { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  if (orders === null) return <p className="text-brand-muted">Loading orders…</p>;
  if (orders.length === 0) return (
    <div className="border border-brand-gold/25 p-10 text-center bg-brand-ivory-soft" data-testid="orders-empty">
      <Package size={32} strokeWidth={1.2} className="mx-auto text-brand-maroon" />
      <p className="font-serif-brand text-2xl text-brand-charcoal mt-4">No orders yet</p>
      <p className="text-sm text-brand-muted mt-2">Your treasures will show up here after your first order.</p>
      <Link to="/shop" className="btn-primary inline-block mt-6">Shop Now</Link>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.order_number} className="border border-brand-gold/25 p-6 bg-brand-ivory-soft" data-testid={`order-${o.order_number}`}>
          <div className="flex flex-wrap justify-between gap-3 pb-4 border-b border-brand-gold/20">
            <div>
              <p className="label-tiny text-brand-gold">{o.status.toUpperCase()}</p>
              <p className="font-serif-brand text-xl text-brand-charcoal mt-1">Order {o.order_number}</p>
              <p className="text-xs text-brand-muted mt-1">Placed on {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
            </div>
            <div className="text-right">
              <p className="label-tiny text-brand-gold">Total</p>
              <p className="font-serif-brand text-xl text-brand-maroon mt-1">{formatINR(o.total)}</p>
            </div>
          </div>
          <div className="mt-4 grid gap-3">
            {o.items.slice(0, 3).map((it, i) => (
              <div key={i} className="flex items-center gap-3">
                <img src={it.image} alt="" className="w-14 h-14 object-cover" />
                <div className="flex-1 text-sm"><p className="font-medium text-brand-charcoal">{it.name}</p><p className="text-xs text-brand-muted">Qty {it.quantity}</p></div>
                <p className="text-sm">{formatINR(it.price * it.quantity)}</p>
              </div>
            ))}
            {o.items.length > 3 && <p className="text-xs text-brand-muted">+ {o.items.length - 3} more items</p>}
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to={`/track-order`} state={{ prefill: o.order_number }} className="text-xs tracking-[0.2em] uppercase font-medium px-4 py-2 border border-brand-maroon text-brand-maroon hover:bg-brand-maroon hover:text-brand-ivory transition-colors duration-200">Track</Link>
          </div>
        </div>
      ))}
    </div>
  );
};

const AddressesTab = () => {
  const [addresses, setAddresses] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "Home", full_name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/me/addresses", { withCredentials: true }).then((r) => setAddresses(r.data)).catch(() => setAddresses([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/me/addresses", form, { withCredentials: true });
      toast.success("Address saved");
      setShowForm(false);
      setForm({ label: "Home", full_name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false });
      load();
    } catch { toast.error("Could not save address"); }
  };

  const del = async (id) => {
    try { await api.delete(`/me/addresses/${id}`, { withCredentials: true }); load(); toast.success("Address removed"); } catch {}
  };

  if (addresses === null) return <p className="text-brand-muted">Loading addresses…</p>;

  return (
    <div data-testid="addresses-tab">
      <div className="flex items-center justify-between mb-6">
        <div><p className="label-tiny text-brand-gold">Delivery</p><h2 className="font-serif-brand text-2xl text-brand-charcoal mt-1">Saved Addresses · {addresses.length}</h2></div>
        <button onClick={() => setShowForm(!showForm)} className="text-xs tracking-[0.2em] uppercase font-medium px-4 py-2 border border-brand-maroon text-brand-maroon hover:bg-brand-maroon hover:text-brand-ivory transition-colors duration-200 flex items-center gap-2" data-testid="add-address-btn"><Plus size={14} /> New</button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-brand-gold/30 bg-brand-ivory-soft p-6 mb-6 space-y-3" data-testid="address-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input required placeholder="Label (Home/Office)" value={form.label} onChange={(e) => set("label", e.target.value)} className="bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
            <input required placeholder="Full Name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
            <input required placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
          </div>
          <input required placeholder="Address Line 1" value={form.line1} onChange={(e) => set("line1", e.target.value)} className="w-full bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
          <input placeholder="Address Line 2 (optional)" value={form.line2} onChange={(e) => set("line2", e.target.value)} className="w-full bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
          <div className="grid md:grid-cols-3 gap-3">
            <input required placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
            <input required placeholder="State" value={form.state} onChange={(e) => set("state", e.target.value)} className="bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
            <input required placeholder="Pincode" value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className="bg-brand-ivory border border-brand-gold/40 px-3 py-2.5 outline-none focus:border-brand-maroon" />
          </div>
          <label className="flex items-center gap-2 text-sm text-brand-charcoal"><input type="checkbox" checked={form.is_default} onChange={(e) => set("is_default", e.target.checked)} /> Set as default</label>
          <button type="submit" className="btn-primary" data-testid="save-address-btn">Save Address</button>
        </form>
      )}

      {addresses.length === 0 && !showForm ? (
        <div className="border border-brand-gold/25 p-10 text-center bg-brand-ivory-soft">
          <MapPin size={32} strokeWidth={1.2} className="mx-auto text-brand-maroon" />
          <p className="font-serif-brand text-2xl text-brand-charcoal mt-4">No saved addresses</p>
          <p className="text-sm text-brand-muted mt-2">Add one for a faster checkout next time.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} className="border border-brand-gold/30 bg-brand-ivory-soft p-5 relative" data-testid={`address-${a.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="label-tiny text-brand-gold">{a.label}{a.is_default && " · Default"}</span>
                <button onClick={() => del(a.id)} className="text-brand-muted hover:text-brand-maroon" aria-label="Delete"><Trash2 size={14} /></button>
              </div>
              <p className="font-medium text-brand-charcoal">{a.full_name}</p>
              <p className="text-sm text-brand-muted mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
              <p className="text-sm text-brand-muted">{a.city}, {a.state} - {a.pincode}</p>
              <p className="text-sm text-brand-muted mt-2">📞 {a.phone}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WishlistTab = () => {
  const { wishlist, toggleWishlist, addToCart } = useShop();
  if (wishlist.length === 0) return (
    <div className="border border-brand-gold/25 p-10 text-center bg-brand-ivory-soft">
      <Heart size={32} strokeWidth={1.2} className="mx-auto text-brand-maroon" />
      <p className="font-serif-brand text-2xl text-brand-charcoal mt-4">Wishlist is empty</p>
      <Link to="/shop" className="btn-primary inline-block mt-6">Browse</Link>
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wishlist-tab">
      {wishlist.map((w) => (
        <div key={w.id} className="border border-brand-gold/25 bg-brand-ivory-soft overflow-hidden">
          <Link to={`/product/${w.slug}`} className="block aspect-square"><img src={w.image} alt={w.name} className="w-full h-full object-cover" /></Link>
          <div className="p-4">
            <p className="font-serif-brand text-brand-charcoal">{w.name}</p>
            <p className="text-brand-maroon font-semibold mt-1">{formatINR(w.price)}</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => addToCart({ id: w.id, slug: w.slug, name: w.name, price: w.price, images: [w.image] })} className="flex-1 text-xs tracking-[0.2em] uppercase py-2 border border-brand-charcoal/70 hover:bg-brand-maroon hover:text-brand-ivory hover:border-brand-maroon">Add</button>
              <button onClick={() => toggleWishlist({ id: w.id })} className="px-3 border border-brand-gold/40 hover:border-brand-maroon"><Trash2 size={14} /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
